import {Alert, Chip, Tooltip, Typography} from '@mui/material';
import {EditNoteRounded} from '@mui/icons-material';
import moment from 'moment';
import {useTranslation} from 'react-i18next';
import {AttendanceRecordSource} from '../../backend/enums/attendance-record-source';
import {AttendanceTrackerSessionPoint} from '../../backend/responses/attendance-tracker-session';
import {translations} from '../../i18n/translations';

type AttendanceManualBadgeProps = {
  point?: AttendanceTrackerSessionPoint;
  /** What the label says is registered by the user: only the entry, only the exit, or the record in general */
  movement?: 'entry' | 'exit';
};

/**
 * Tag that says a record was registered by someone else, on behalf of the professional. It shows nothing for the
 * records registered by the professional.
 */
export const AttendanceManualBadge = ({point, movement}: AttendanceManualBadgeProps) => {
  const {t} = useTranslation();
  const texts = translations.daily_care_log.attendance_tracker.manual;

  if (point?.source !== AttendanceRecordSource.Manual) {
    return null;
  }

  const name = point.registeredBy?.name || t(texts.unknown_user);
  const label =
    movement === 'entry'
      ? texts.entry_registered_by
      : movement === 'exit'
        ? texts.exit_registered_by
        : texts.registered_by;

  return (
    <Tooltip title={point.reason ? t(texts.reason, {reason: point.reason}) : ''}>
      <Chip
        className="attendance-manual-badge"
        size="small"
        icon={<EditNoteRounded />}
        label={t(label, {name})}
        sx={{
          maxWidth: '100%',
          backgroundColor: '#fff3d6',
          color: '#8a5a00',
          fontWeight: 500,
          '& .MuiChip-icon': {color: '#8a5a00'},
        }}
      />
    </Tooltip>
  );
};

type AttendanceManualNoticeProps = {
  point?: AttendanceTrackerSessionPoint;
};

/** Says, in the details of a record, that it was registered by someone else on behalf of the professional, and why */
export const AttendanceManualNotice = ({point}: AttendanceManualNoticeProps) => {
  const {t} = useTranslation();
  const texts = translations.daily_care_log.attendance_tracker.manual;

  if (point?.source !== AttendanceRecordSource.Manual) {
    return null;
  }

  return (
    <Alert severity="warning" icon={<EditNoteRounded />}>
      <Typography sx={{fontSize: '14px', fontWeight: 500}}>
        {t(texts.notice, {
          name: point.registeredBy?.name || t(texts.unknown_user),
          date: moment(point.createdAt).format('DD/MM/YYYY HH:mm'),
        })}
      </Typography>
      {point.reason && (
        <Typography sx={{fontSize: '14px', overflowWrap: 'anywhere'}}>
          {t(texts.reason, {reason: point.reason})}
        </Typography>
      )}
    </Alert>
  );
};
